import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, X, ChevronDown, ChevronUp, HardHat } from 'lucide-react';

const TIERS = [
  { id: 'basic', name: 'Basic' },
  { id: 'advanced', name: 'Advanced' },
  { id: 'professional', name: 'Professional' },
];

const FEATURE_COMPARISON = [
  { 
    category: 'Core Features',
    features: [
      { name: 'Asset Management', basic: true, advanced: true, professional: true },
      { name: 'Operator Management', basic: true, advanced: true, professional: true },
      { name: 'Job Scheduling', basic: true, advanced: true, professional: true },
      { name: 'Digital Dockets', basic: true, advanced: true, professional: true },
      { name: 'Customer & Site Management', basic: true, advanced: true, professional: true },
      { name: 'Worker Types', basic: true, advanced: true, professional: true },
      { name: 'System Forms', basic: true, advanced: true, professional: true },
      { name: 'Subcontractors', basic: '1 (Own company)', advanced: 'Unlimited', professional: 'Unlimited' },
    ]
  },
  {
    category: 'Advanced Features',
    features: [
      { name: 'Standard Reports', basic: false, advanced: true, professional: true },
      { name: 'Compliance Tracking', basic: false, advanced: true, professional: true },
      { name: 'Compliance Packs', basic: false, advanced: true, professional: true },
      { name: 'Timesheets', basic: false, advanced: true, professional: true },
      { name: 'Custom Forms Builder', basic: false, advanced: true, professional: true },
      { name: 'Team Messaging', basic: false, advanced: true, professional: true },
      { name: 'Quotes', basic: false, advanced: true, professional: true },
    ]
  },
  {
    category: 'Professional Features',
    features: [
      { name: 'Quote Templates', basic: false, advanced: false, professional: true },
      { name: 'Premium Reports', basic: false, advanced: false, professional: true },
      { name: 'AI-Powered Features', basic: false, advanced: false, professional: true },
      { name: 'Smart Suggestions', basic: false, advanced: false, professional: true },
      { name: 'OCR Docket Scanning', basic: false, advanced: false, professional: true },
    ]
  },
  {
    category: 'Storage & Support',
    features: [
      { name: 'Document Storage', basic: '10 GB', advanced: '50 GB', professional: '100 GB' },
      { name: 'Storage Add-ons', basic: true, advanced: true, professional: true },
      { name: 'Help Centre', basic: true, advanced: true, professional: true },
      { name: 'Support', basic: 'Standard', advanced: 'Priority', professional: 'Priority+' },
    ]
  }
];

const renderValue = (value) => {
  if (value === true) {
    return <Check className="h-5 w-5 text-green-500 mx-auto" />;
  } else if (value === false) {
    return <X className="h-5 w-5 text-zinc-600 mx-auto" />;
  } else if (value === 'soon') {
    return <span className="text-xs text-amber-500 font-medium">Soon</span>;
  }
  return value;
};

export default function Features() {
  const [expandedCategories, setExpandedCategories] = useState(
    FEATURE_COMPARISON.reduce((acc, cat) => ({ ...acc, [cat.category]: true }), {})
  );

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center py-12 px-4 bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div className="w-full max-w-5xl relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <div className="h-14 w-14 bg-black rounded-sm overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_3134b3b3-be40-4138-91b9-92f518baf2f3/artifacts/jfduu0wk_EQUIPR%20Icon300X300px.png" 
                alt="EQUIPR" 
                className="w-full h-full object-cover" 
              />
            </div>
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-3">Feature Comparison</h1>
          <p className="text-zinc-400 text-lg">See what's included in every EQUIPR CORE plan</p>
        </div>

        {/* Feature matrix */}
        <Card className="border border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed" data-testid="feature-matrix">
              <colgroup>
                <col className="w-[40%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="px-4 py-4 text-left text-sm font-medium text-zinc-500">Feature</th>
                  {TIERS.map((tier) => (
                    <th key={tier.id} className="px-4 py-4 text-center">
                      <span className="text-sm font-bold text-white">{tier.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_COMPARISON.map((category) => (
                  <>
                    <tr
                      key={`cat-${category.category}`}
                      className="bg-zinc-900/80 cursor-pointer hover:bg-zinc-900"
                      onClick={() => toggleCategory(category.category)}
                    >
                      <td colSpan={4} className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {expandedCategories[category.category]
                            ? <ChevronUp className="h-4 w-4 text-zinc-400" />
                            : <ChevronDown className="h-4 w-4 text-zinc-400" />
                          }
                          <span className="text-sm font-semibold text-[#ffd108]">{category.category}</span>
                        </div>
                      </td>
                    </tr>
                    {expandedCategories[category.category] && category.features.map((feature) => (
                      <tr key={feature.name} className="border-t border-zinc-800/50 hover:bg-zinc-900/30">
                        <td className="px-4 py-3 text-sm text-zinc-300">{feature.name}</td>
                        <td className="px-4 py-3 text-center text-sm text-zinc-300">{renderValue(feature.basic)}</td>
                        <td className="px-4 py-3 text-center text-sm text-zinc-300">{renderValue(feature.advanced)}</td>
                        <td className="px-4 py-3 text-center text-sm text-zinc-300">{renderValue(feature.professional)}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* CTA */}
        <div className="text-center mt-10 space-y-4">
          <div>
            <Link to="/login">
              <Button className="btn-volt px-8 py-3 text-base w-64" data-testid="features-signup-btn">
                <HardHat className="h-5 w-5 mr-2" />
                SIGN UP
              </Button>
            </Link>
          </div>
          <div>
            <a href="https://www.equiprsoftware.com/book-a-demo" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="px-8 py-3 text-base w-64 bg-card" data-testid="features-request-demo-btn">
                REQUEST A DEMO
              </Button>
            </a>
          </div>
          <div>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
